import { FastField, Field, Formik } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import * as React from "react"
import { Appearance, ImageStyle, TextInput, useColorScheme, View, ViewStyle } from "react-native"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { color } from "../../../theme"
import config from "../../../utils/config"
import { AutoImage } from "../../auto-image/auto-image"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { Picker } from "../../picker/picker"
import { Text } from "../../text/text"
import { FormProps } from "../form"


const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const IMG: ImageStyle = {
  width: 68,
  height: 68,
  borderRadius: 60,
  borderColor: color.palette.gray200,
  borderWidth: 3,
}

const AVATAR_WRAPPER: ViewStyle = {
  ...FORM_DROPDOWN,
  flexDirection: 'row',
  paddingTop: 20
}

const INFO: ViewStyle = {
  flex: 1
}

const role = [
  { id: 3, name: 'owner' },
  { id: 10, name: 'admin' },
  { id: 4, name: 'operator' },
  { id: 11, name: 'laboran' },
  { id: 5, name: 'viewer' }
];

const { BASE_URL } = config();


export interface AddMemberFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    email?: string
    role_id?: number
    user?: any
  }
}

const EmailInput = (
  { name, input = {}, nextInput = {}, colorScheme = useColorScheme(), formControlProps } :
  { name: string, input: any, nextInput: any, colorScheme?: "light" | "dark", formControlProps?: FormControlProps}
) => (
  <Field name={name}>
    {({ field, form: { handleChange, handleBlur, handleSubmit, isValid }, meta }) => {
      return(
        <FormControl
          isRequired
          isInvalid={meta.touched && !!meta.error}
          labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
          labelPreset="title3"
            colorScheme={colorScheme}
            inputProps={{
              testID: `jala:id/${field.name}-input`,
              ref: input[field.name],
              autoCapitalize: "none",
              autoComplete: "email",
              autoCorrect: false,
              textContentType: "emailAddress",
              keyboardType: "email-address",
              returnKeyType: nextInput[field.name] ? "next" : "go",
              onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
              placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
              defaultValue: meta.initialValue,
              onChangeText: handleChange(field.name),
              onBlur: handleBlur(field.name)
            }}
            errorMessage={meta.error}
            style={FORM_DROPDOWN}
            {...formControlProps}
        />
        )
      }
    }
  </Field>
)

const PickerField = (
  { name, colorScheme = useColorScheme(), formControlProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => {
      return (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`connectedFarm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        // labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <Picker
          data={role}
          colorScheme={colorScheme}
          placeholderTx={`connectedFarm.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
          }}
          renderSelectedItemText={(item) => item && translate(`connectedFarm.${_.camelCase(item.name)}` as TxKeyPath)}
          isUseSearch={false}
        />
      </FormControl>
    )}}
  </FastField>
)

/**
 * Describe your component here
 */
export const AddMemberForm = observer(function AddMemberForm(props: AddMemberFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props

  const input = {
    email: React.useRef<TextInput>(),
    role_id: React.useRef<TextInput>()
  }

  const nextInput = {
    email: () => input.role_id.current && input.role_id.current.focus()
  }

  const validationSchema = yup.object({
    email: yup.string().email().required().label(translate("registerForm.email")),
    role_id: yup.number().required().label(translate("samplingForm.farmId"))
  })

  const Avatar = () => {
    const { user } = initialValues;
    const image = `${BASE_URL}/img/cache/medium/${user.avatar}`;
    return(
      <View style={AVATAR_WRAPPER}>
        <AutoImage source={{ uri: image }} style={IMG} />
        <View style={INFO}>
          <Text preset="title2" text={user.name} colorScheme={colorScheme} style={FORM_DROPDOWN} />
          <Text preset="body1" text={user.email} colorScheme={colorScheme} style={FORM_DROPDOWN} />
          <PickerField name="role_id" colorScheme={colorScheme} formControlProps={{ isRequired: true }} />
        </View>
      </View>
    )
  }

  return (
    <Formik
      initialValues={{
        email: "",
        role_id: null,
        route: "add",
        ...initialValues
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues }) => (
        <>
          {initialValues.email ? <Avatar /> : (
            <>
              <EmailInput name="email" input={input} nextInput={nextInput} colorScheme={colorScheme} formControlProps={{ isDisabled: !!initialValues.email }}  />
              <PickerField name="role_id" colorScheme={colorScheme} formControlProps={{ isRequired: true }} />
            </>
          )}
        </>
      )}
    </Formik>
  )
})

AddMemberForm.displayName = 'AddMemberForm'
